import type { Id } from "@repo/convex/dataModel";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { useConsultationTranscript } from "@/features/clarification/hooks";

function formatTranscriptBody(message: {
  bodyText: string;
  structuredResponseJson?: { optionId?: string; freeText?: string } | null;
}) {
  if (message.bodyText.trim().length > 0) return message.bodyText;
  const structured = message.structuredResponseJson;
  if (!structured) return message.bodyText;

  const parts = [
    structured.optionId ? `Selected option: ${structured.optionId}` : null,
    structured.freeText?.trim() ? `Added: ${structured.freeText.trim()}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(". ") : "Submitted a structured response.";
}

const TRANSCRIPT_NOTE_CLASS = "mt-2 pl-3 text-[11px] text-muted-foreground";

/**
 * Inline Teams transcript (LAT-626). Rendered only when the user expands a
 * consultation summary, so the underlying query fires lazily. Styled as the
 * indented conversation in the design (Pencil node AEShP).
 */
export function ConsultationTranscript({ teamsChatId }: { teamsChatId: Id<"teamsChats"> }) {
  const messages = useConsultationTranscript(teamsChatId);

  return (
    <AsyncBoundary
      data={messages}
      loading={<p className={TRANSCRIPT_NOTE_CLASS}>Loading transcript…</p>}
      empty={<p className={TRANSCRIPT_NOTE_CLASS}>No Teams replies captured yet.</p>}
    >
      {(messages) => (
        <div className="mt-2 space-y-2 border-l-2 border-border pl-3">
          {messages.map((message) => {
            const speaker =
              message.fromDisplayName ?? (message.direction === "outbound" ? "Claudia" : "Teams");
            return (
              <div key={message._id} className="text-xs">
                <div className="mb-0.5 flex items-center gap-2 text-muted-foreground">
                  <span className="truncate font-medium text-foreground">{speaker}</span>
                  <time dateTime={new Date(message.sentAt).toISOString()} className="text-[10px]">
                    {new Date(message.sentAt).toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </time>
                </div>
                <p className="text-foreground/80">{formatTranscriptBody(message)}</p>
              </div>
            );
          })}
        </div>
      )}
    </AsyncBoundary>
  );
}
